import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModuleActionsSlot } from "../ModuleActionsSlot/ModuleActionsSlot";

export interface PageHeadProps {
  /** Where this page lives, shown as plain text above the title (e.g. ["Admin Center"]). */
  breadcrumb?: string[];
  title: string;
  /** Keep the title for screen readers only: for a module's main page, where the module header already names it.
   *  The actions then move up into the module header, on the right. */
  titleHidden?: boolean;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

/** Every view opens with PageHead: optional breadcrumb + title + subtitle + actions on the right. */
export function PageHead({ titleHidden, ...props }: PageHeadProps): React.JSX.Element {
  return titleHidden ? <HiddenTitleHead title={props.title} actions={props.actions} /> : <VisibleHead {...props} />;
}

function VisibleHead({ breadcrumb, title, subtitle, actions }: Omit<PageHeadProps, "titleHidden">): React.JSX.Element {
  return (
    <div className="nb-page-head">
      <div className="nb-page-head-text">
        <Breadcrumb items={breadcrumb} />
        <h1>{title}</h1>
        {subtitle ? <p className="nb-page-head-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="nb-page-actions">{actions}</div> : null}
    </div>
  );
}

function Breadcrumb({ items }: { items: string[] | undefined }): React.JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <nav className="nb-page-crumbs" aria-label="Breadcrumb">
      <ol>
        {items.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
    </nav>
  );
}

/** The title is for screen readers; the actions move into the module header when there is one. */
function HiddenTitleHead({ title, actions }: Pick<PageHeadProps, "title" | "actions">): React.JSX.Element {
  const slot = useModuleActionsSlot()?.[0];
  const buttons = actions ? <div className="nb-page-actions">{actions}</div> : null;
  return (
    <>
      <h1 className="nb-sr-only">{title}</h1>
      {buttons && slot ? createPortal(buttons, slot) : buttons ? <div className="nb-page-head is-actions-only">{buttons}</div> : null}
    </>
  );
}
